/**
 * live-preview-assist.js — «Moodbook AI», the text helper: one amber button
 * in the field's toolbar, the ideas panel IN PLACE of the text inside the
 * same box (the «Command Center»), and three jobs, two of them done without
 * leaving the browser.
 *
 * v2 was decided by Stelios on 2026-09-15 after four design revisions
 * (tools/text-assist/SPEC_v2.md is the contract); v2.1 (2026-09-16, §8) gave
 * the helper its name and turned ideas into tone chips whose result goes
 * straight onto the cover; v2.2 (2026-09-17, §9) moved the entry INSIDE the
 * field and put the tones in a bottom sheet / popover; the Command Center
 * (2026-09-18/19, §10 — Claude Design's handoff, his final design) replaced
 * that shell: no sheet, no popover, no overlay — ONE box in the page flow
 * that keeps its height and swaps what it shows. What the customer sees:
 *
 *   ╭──────────────────────────────────────────────╮
 *   │ Γράψε το κείμενο που θέλεις να τυπώσουμε ή   │  ← the field (State 1)
 *   │ πάτα το ✨ Δώσε μου ιδέες …                  │
 *   │                                              │
 *   │ (status line — the waiting ladder, «Μπήκε…») │
 *   │ [≡] [↶] [↷] [− Aa +]        [ ✨ Δώσε μου ιδέες ]│  ← the toolbar, the pill at the right
 *   ╰──────────────────────────────────────────────╯
 *
 *   ideas   — the pill swaps the ideas panel in for the text (State 2): the
 *             title («✨ Βρες το τέλειο μήνυμα», ✕), a one-line box for names
 *             and dates (optional; pre-filled with what the field holds, its
 *             placeholder «π.χ. Μαρία & Νίκος, 20/08/2027» — the product's
 *             example), «Διάλεξε ύφος:» and the product's three tones as 48px
 *             chips, plus «✏️ Άλλο ύφος...» — two rows at most on a phone,
 *             the tones ordered by width to fit, the fixed chip always last
 *             (Stelios, 2026-09-20 evening). The keyboard does NOT open: the
 *             tap is on a tone, and it is the send. The panel swaps out at
 *             once; while the model works the text is under a skeleton, the
 *             toolbar dims and the status line climbs the waiting ladder
 *             («Σκέφτομαι…» → «Σκέφτομαι ακόμα…» → «Σχεδόν έτοιμο…»,
 *             3 s a step, the last one stays); the first of the three answers
 *             goes STRAIGHT INTO THE FIELD as «✦ Το κείμενο είναι έτοιμο.»
 *             shows — the cover is the preview, there is no card — and the
 *             pill reads «↩️ Αναίρεση» (navy outline). Undo puts the
 *             customer's text and size back and the pill is «✨» again; the
 *             same tone tapped again hands out the next answer with no call
 *             (three per call), the fourth tap calls again with the seen
 *             ones excluded; another tone is its own set. Typing over an
 *             inserted text makes it the customer's own. «✏️ Άλλο ύφος...»
 *             is the escape hatch: the chips give way to «‹», a box for a
 *             style in the customer's own words and «✨ Δημιουργία»; the
 *             words go to the Worker as `tone` + `toneCustom`, fenced as
 *             data, never to analytics.
 *   accents — 1.5 s after the last keystroke the print queue's tone rule
 *             runs locally; if a tone is missing, one calm line under the
 *             box asks «θέλεις να τα δεις;» — «Δείξε μου» is the tap that
 *             sends. The correction is the box's third content: a diff
 *             («Πρόσθεσα τον τόνο…»), read before it is applied.
 *
 * WHAT DID NOT CHANGE. This is still a separate file that touches the element
 * through two doors: it READS `input.value`, `config`, the box and the font,
 * and it WRITES by setting `input.value` and dispatching `input` — the same
 * event a keystroke fires — so the fit, the floor, the descriptor, the gallery
 * flip and ↶ ↷ all run as for typing. Nothing in the element's DOM is moved:
 * the box and its toolbar are authored in the snippet; this file APPENDS the
 * pill to the toolbar, the status line and the panel into the box, the
 * skeleton over the text, and the accents line after the box. The theme's own
 * controls keep their nodes and their listeners.
 *
 * ⛔ 2026-09-22 — «Τακτοποίησέ το» is GONE. It sat under the box in a row of
 * its own, offered on any text of two words, and Stelios's verdict on the
 * first cross-sell preview was that it «δεν προσφέρει κάτι και καταλαμβάνει
 * γραμμή». The rule engine it called stays, because that is what lays out the
 * model's answers (fitIdea/layoutSize in live-preview-tidy.js); what is gone
 * is the chip, its row and the nudge that pointed at it.
 *
 * ⚠ THE TEXT IS A PERSONAL NAME. It leaves the browser ONLY on a tap the
 * customer reads as sending: a tone, «✨ Δημιουργία», «Δείξε μου». Never on
 * keystroke, blur, timer or load — the tidy and accent checks run locally
 * precisely so that they need no call, and the harness asserts that typing
 * and the checks send nothing. What is typed in the panel's box is material
 * for the model and never touches the field, the cover or the descriptor.
 *
 * Analytics: `lp_assist` with lp_mode (ideas|spell), lp_result,
 * lp_shape (a rule-only guess: empty|name|sentence), lp_source and, for a
 * tone tap, lp_tone (its position, 1–3; one past the chips = the customer's
 * own style) — never a character of the text, nor of the style.
 */
(function () {
  'use strict';

  var T = {
    label: 'Moodbook AI',
    ideas: '✨ Δώσε μου ιδέες',
    undoChip: '↩️ Αναίρεση',
    sheetTitle: '✨ Βρες το τέλειο μήνυμα',
    askLabel: 'Ονόματα ή Ημερομηνίες (προαιρετικά)',
    eg: 'π.χ. ',
    pick: 'Διάλεξε ύφος:',
    defaultTones: ['❤️ Τρυφερό', '🥂 Κλασικό', '😄 Με χιούμορ'],
    // The escape hatch (Stelios, 2026-09-18): the fourth chip opens a small
    // box for a style in the customer's own words — the chips cover the 80 %,
    // this covers the inside joke and the uncle in America. The placeholder
    // is the tested one (his word, 2026-09-19).
    customTone: '✏️ Άλλο ύφος...',
    customTitle: 'Το δικό σου ύφος',
    customPlaceholder: 'π.χ. σαν ποίημα, απόφθεγμα, στα αγγλικά, με ρίμα, πολύ επίσημο…',
    customGo: '✨ Δημιουργία',
    back: '‹',
    backLabel: 'Πίσω στα έτοιμα ύφη',
    // The waiting ladder (the handoff's §5.5, his decision Α1 in place of
    // streaming): three steps 3 s apart, the last one stays; then the answer.
    // The third step was «Σχεδόν είμαι έτοιμος…» — with the name in front it
    // clipped on an iPhone SE (stress run, 2026-09-20).
    ladder: ['Σκέφτομαι…', 'Σκέφτομαι ακόμα…', 'Σχεδόν έτοιμο…'],
    ready: '✦ Το κείμενο είναι έτοιμο.',
    wait: { spell: 'Ελέγχω το κείμενο…' },
    stillWaiting: 'ακόμα λίγο…',
    // The status line is ONE line (20px) and the box never moves — so every
    // line here fits a 320px phone at 14px: ~40 characters. The stress run of
    // 2026-09-20 (tools/ios-safari/command-center-stress.mjs, iPhone 13) found
    // the old «Μπήκε στο εξώφυλλο. Αν δεν σου κάνει, πάτα «↩️ Αναίρεση»…» on two
    // lines, and the toolbar jumping 20px when the line cleared. The undo is
    // the pill right under the line; the hint said what the eye already sees.
    // The measure: 262px at 320px wide, Neutra Text 6.96 px a letter at 14px
    // (research/07_chipfit_neutra_results.txt) — 37 characters; the harness
    // counts them, the stress run measures them on the real theme.
    error: 'Ο βοηθός δεν απάντησε· δοκίμασε ξανά.',
    noFit: 'Οι ιδέες δεν χωρούν· λιγότερα λόγια.',
    retry: 'Δοκίμασε ξανά',
    close: 'Κλείσε',
    inserted: 'Μπήκε στο εξώφυλλο.',
    insertedTone: 'Μπήκε στο εξώφυλλο.',
    restored: 'Το κείμενό σου επέστρεψε.',
    undo: '↶ Αναίρεση',
    accents: 'Εντόπισα κάποια λαθάκια στο κείμενο — θέλεις να τα δεις;',
    show: 'Δείξε μου',
    fineByMe: 'Είναι ΟΚ',
    fixed: 'Το διόρθωσα',
    fix: 'Διόρθωσέ το',
    leave: 'Άσ’ το όπως ήταν',
    fine: 'Η ορθογραφία είναι εντάξει ✓',
    ok: 'Εντάξει',
    spent: 'Ο βοηθός έδωσε ό,τι είχε για σήμερα.',
  };
  var CALLS_PER_PAGE = 10;
  // Opus answers ideas in 3–5 s, occasionally more under load; 25 s (the
  // Worker retries once inside that), with the ladder keeping the wait held.
  var TIMEOUT_MS = 25000;
  var STILL_MS = 7000;         // the correction's wait line gains «ακόμα λίγο…»
  var LADDER_MS = 3000;        // one step of the waiting ladder
  var READY_MS = 1800;         // «✦ Το κείμενο είναι έτοιμο.» before «Μπήκε στο εξώφυλλο…»
  var IDLE_MS = 1500;          // the pause after which tidy and accents look at the text — locally
  var STATUS_MS = 8000;        // «Μπήκε στο εξώφυλλο…»
  var RESTORED_MS = 6000;      // «Το κείμενό σου επέστρεψε…»
  var ERROR_MS = 7000;
  var MAX_TONES = 5;
  var MAX_ASK_CHARS = 160;
  var MAX_CUSTOM_CHARS = 80;   // the Worker's cleanTone cap
  var MAX_EXCLUDE = 6;         // the Worker's cap on `exclude`
  var SCROLL_MARGIN = 12;      // the box's breathing room when it is scrolled into view
  var NBSP = '\u00a0';
  var MOBILE = '(max-width: 749px)';

  function init(el) {
    if (!el || el._lpAssist) return;
    var cfg = el.config || {};
    if (!cfg.assistUrl) return;
    var input = el.input || el.querySelector('[data-lp-input]');
    if (!input) return;
    el._lpAssist = true;
    var Tidy = window.LivePreviewTidy || null;   // local layout + accents; the helper degrades to ideas-only without it
    // The box is authored in the snippet (the harness mirrors it): the text
    // in its own wrapper, then the toolbar. Nothing is wrapped here.
    var textWrap = input.parentNode;
    var box = (input.closest && input.closest('[data-lp-box]')) || textWrap.parentNode;
    var row = box.querySelector('.lp-controls-row');
    box.classList.add('lp-ai-on');
    var label = cfg.assistLabel || T.label;
    var sheetTitle = cfg.assistSheetTitle || T.sheetTitle;
    var askLabelText = cfg.assistAskLabel || T.askLabel;
    var tones = toneList(cfg.assistTones);

    // ── the pill: at the right end of the toolbar, inside the box ─────────
    var fab = mk('button', 'lp-ai-fab');
    glyphText(fab, T.ideas);
    fab.type = 'button';
    fab.setAttribute('data-lp-assist-fab', 'ideas');
    // A tap on the pill must not move the caret or, on desktop, take focus
    // off the field: the pill acts, the field stays the customer's.
    fab.addEventListener('pointerdown', function (e) { e.preventDefault(); });
    fab.addEventListener('mousedown', function (e) { e.preventDefault(); });
    if (row) row.appendChild(fab); else box.appendChild(fab);

    // ── the status line: inside the box, between the text and the toolbar ─
    // Always there, one line high, so nothing moves when it fills or empties.
    var status = mk('p', 'lp-ai-status');
    status.setAttribute('data-lp-assist-status', '');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.textContent = NBSP;   // never :empty — the shop's base.css hides p:empty, and the line must keep its place
    textWrap.insertAdjacentElement('afterend', status);

    // ── the skeleton over the text while the model works ─────────────────
    var skeleton = mk('div', 'lp-ai-skeleton');
    skeleton.setAttribute('aria-hidden', 'true');
    for (var sk = 0; sk < 3; sk++) skeleton.appendChild(mk('i', ''));
    textWrap.appendChild(skeleton);

    // ── under the box: the accents line ───────────────────────────────────
    var acc = mk('div', 'lp-ai-accents');
    acc.setAttribute('data-lp-assist-accents', '');
    acc.setAttribute('role', 'status');
    acc.hidden = true;
    acc.appendChild(mk('span', 'lp-ai-accents-text', T.accents));
    var accShow = mk('button', 'lp-ai-accents-btn lp-ai-accents-show', T.show);
    accShow.type = 'button';
    accShow.setAttribute('data-lp-assist-accents-show', '');
    var accOk = mk('button', 'lp-ai-accents-btn', T.fineByMe);
    accOk.type = 'button';
    accOk.setAttribute('data-lp-assist-accents-ok', '');
    acc.appendChild(accShow); acc.appendChild(accOk);
    // after the box — and after the «δεν χωράει» note when it sits right there
    var anchor = box;
    var next = box.nextElementSibling;
    if (next && next.hasAttribute && next.hasAttribute('data-lp-note')) anchor = next;
    anchor.insertAdjacentElement('afterend', acc);

    // ── the panel: the box's other content, in place of the text ─────────
    // Two contents share it and never at once: the ideas panel (the box and
    // the tones) and the correction card.
    var sheet = mk('div', 'lp-ai-panel');
    sheet.setAttribute('data-lp-assist-panel', '');
    sheet.setAttribute('role', 'group');
    sheet.setAttribute('aria-label', label);
    sheet.setAttribute('tabindex', '-1');
    sheet.hidden = true;
    var head = mk('div', 'lp-ai-head');
    var title = mk('strong', 'lp-ai-title');
    title.setAttribute('data-lp-assist-title', '');
    var close = mk('button', 'lp-ai-close', '✕');
    close.type = 'button';
    close.setAttribute('aria-label', T.close);
    head.appendChild(title); head.appendChild(close);
    var body = mk('div', 'lp-ai-body');
    sheet.appendChild(head); sheet.appendChild(body);
    box.appendChild(sheet);

    // The ideas panel, built once so the box keeps what the customer typed
    // between openings.
    var panel = mk('div', 'lp-ai-ideas');
    panel.setAttribute('data-lp-assist-ideas', '');
    var askId = 'LpAsk-' + Math.random().toString(36).slice(2, 8);
    var askWrap = mk('div', 'lp-ai-askwrap');
    var askLabel = mk('label', 'lp-ai-ask-label', askLabelText);
    askLabel.setAttribute('for', askId);
    var ask = mk('input', 'lp-ai-ask');
    ask.type = 'text';
    ask.id = askId;
    ask.setAttribute('data-lp-assist-ask', '');
    ask.setAttribute('autocomplete', 'off');
    ask.setAttribute('autocapitalize', 'sentences');
    ask.setAttribute('enterkeyhint', 'done');
    ask.setAttribute('maxlength', String(MAX_ASK_CHARS));
    if (cfg.assistAskExample) ask.placeholder = T.eg + String(cfg.assistAskExample);
    askWrap.appendChild(askLabel); askWrap.appendChild(ask);
    var zone = mk('div', 'lp-ai-zone');
    var pickLine = mk('p', 'lp-ai-pick', T.pick);
    var tonesBox = mk('div', 'lp-ai-tones');
    tonesBox.setAttribute('role', 'group');
    tonesBox.setAttribute('aria-label', T.pick);
    var toneChips = tones.map(function (t, i) {
      var c = chip('tone', t);
      c.classList.add('lp-ai-tone');
      c.setAttribute('data-lp-assist-tone', String(i));
      c.addEventListener('click', function () { pickTone(i); });
      tonesBox.appendChild(c);
      return c;
    });
    // The escape hatch: a fourth chip that swaps the chips for a box.
    var chipCustom = chip('tone', T.customTone);
    chipCustom.classList.add('lp-ai-tone', 'lp-ai-tone-custom');
    chipCustom.setAttribute('data-lp-assist-tone', 'custom');
    chipCustom.addEventListener('click', function () { enterCustom(); });
    tonesBox.appendChild(chipCustom);
    var customBox = mk('div', 'lp-ai-custom');
    customBox.setAttribute('data-lp-assist-custom-box', '');
    customBox.hidden = true;
    var customRow = mk('div', 'lp-ai-custom-row');
    var back = mk('button', 'lp-ai-back', T.back);
    back.type = 'button';
    back.setAttribute('data-lp-assist-custom-back', '');
    back.setAttribute('aria-label', T.backLabel);
    back.title = T.backLabel;
    var custom = mk('input', 'lp-ai-ask lp-ai-custom-input');
    custom.type = 'text';
    custom.setAttribute('data-lp-assist-custom', '');
    custom.setAttribute('autocomplete', 'off');
    custom.setAttribute('autocapitalize', 'sentences');
    custom.setAttribute('enterkeyhint', 'go');
    custom.setAttribute('maxlength', String(MAX_CUSTOM_CHARS));
    custom.setAttribute('aria-label', T.customTitle);
    custom.placeholder = T.customPlaceholder;
    customRow.appendChild(back); customRow.appendChild(custom);
    var go = mk('button', 'lp-ai-go');
    glyphText(go, T.customGo);
    go.type = 'button';
    go.setAttribute('data-lp-assist-custom-go', '');
    customBox.appendChild(customRow); customBox.appendChild(go);
    zone.appendChild(pickLine); zone.appendChild(tonesBox); zone.appendChild(customBox);
    panel.appendChild(askWrap); panel.appendChild(zone);

    // ── state ─────────────────────────────────────────────────────────────
    var open = false;         // the panel is showing
    var sheetMode = '';       // ideas | spell — what it shows
    var busy = false;         // a call in flight
    var inserted = false;     // one of the helper's texts is in the field, unedited — the pill is «↩️ Αναίρεση»
    var roundText = null;     // the last text the tones put in, until the customer undoes it — for «kept» at add-to-cart
    var roundTone = -1;
    var queues = {};          // tone + material → { list: the answers not yet shown, shown: all of them }
    var seenFor = {};         // material → every answer shown for it under ANY tone, in order — what a new call must not repeat (2026-09-20)
    var askFromField = '';    // what the box was last pre-filled with from the field — a box the customer edited is never overwritten
    var customMode = false;   // the escape hatch is open: the style box instead of the chips (remembered across openings)
    var mode = null;          // ideas | spell
    var calls = 0;            // model calls this page visit
    var dismissed = {};       // accent lines the customer answered «Είναι ΟΚ» to, by text
    var statusKind = '';
    var statusTimer = null;
    var ladderTimer = null;   // the waiting ladder's step timer
    var readyTimer = null;    // «έτοιμο» → «Μπήκε στο εξώφυλλο…»
    var idleTimer = null;
    var prevText = null;      // what the field held before the helper's write — for «↩️ Αναίρεση» and the status line's ↶
    var writing = false;      // an `input` event the helper itself fired, not a keystroke
    var inflight = null;      // the AbortController of the call in flight — ✕ on the card drops it

    // ── keeping the box in view (the handoff's Α3) ────────────────────────
    // The panel lives in the page flow, so a tap on the pill with the box in
    // the lower half of the phone's screen would leave the chips under the
    // sticky bar or off-screen; the same for the style box under the
    // keyboard. A gentle scroll of the WINDOW — never scrollIntoView, which
    // moves every scrolling ancestor in Dawn. The visual viewport is what the
    // customer sees above the keyboard.
    var vv = window.visualViewport || null;
    function ensureVisible(target) {
      try {
        var r = target.getBoundingClientRect();
        var top = vv ? vv.offsetTop : 0;
        var height = vv ? vv.height : window.innerHeight;
        var floor = top + height - (target === box ? stickyBarHeight() : 0) - SCROLL_MARGIN;
        var ceiling = top + headerHeight() + SCROLL_MARGIN;
        var dy = 0;
        if (r.bottom > floor) dy = r.bottom - floor;
        if (r.top - dy < ceiling) dy = r.top - ceiling;   // the top edge wins: the title is what they read first
        if (Math.abs(dy) < 2) return;
        var y = Math.max(0, window.scrollY + dy);
        try { window.scrollTo({ top: y, left: window.scrollX, behavior: 'smooth' }); }
        catch (e) { window.scrollTo(window.scrollX, y); }
      } catch (e) { /* a page that cannot be measured is left where it is */ }
    }
    // The shop's header when it is sticky and showing: the box must land
    // under it, not behind it.
    function headerHeight() {
      var h = document.querySelector('sticky-header, .shopify-section-header-sticky, .section-header');
      if (!h) return 0;
      var cs = getComputedStyle(h);
      if (cs.position !== 'fixed' && cs.position !== 'sticky') return 0;
      // Dawn's header comes back on any scroll up — the room is needed
      // whether it shows at this instant or not.
      return Math.round(h.getBoundingClientRect().height) || 0;
    }
    // The phone's sticky add-to-cart bar (an app: #moodbook-mobile-sticky-atc,
    // ~68px, slides in with .is-visible) covers the bottom of the screen;
    // found by shape, not only by id, so a renamed app still works.
    function stickyBarHeight() {
      var bar1 = document.getElementById('moodbook-mobile-sticky-atc');
      var cands = bar1 ? [bar1] : [];
      if (!cands.length) {
        var all = document.querySelectorAll('[id*="sticky"], [class*="sticky"]');
        for (var i = 0; i < all.length; i++) if (!box.contains(all[i])) cands.push(all[i]);
      }
      var best = 0;
      for (var j = 0; j < cands.length; j++) {
        var e = cands[j];
        var cs = getComputedStyle(e);
        if (cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden') continue;
        var r = e.getBoundingClientRect();
        if (r.width < window.innerWidth * 0.6 || r.height < 30 || r.height > 200) continue;
        if (r.top >= window.innerHeight - 1) continue;
        if (Math.abs(r.bottom - window.innerHeight) > 2) continue;
        best = Math.max(best, Math.round(window.innerHeight - r.top));
      }
      return best;
    }
    // While the style box has focus, the keyboard's arrival shrinks the
    // visual viewport: keep the box above it.
    if (vv) vv.addEventListener('resize', function () { if (open && document.activeElement === custom) ensureVisible(customBox); });

    // ── the status line ───────────────────────────────────────────────────
    // One line, always in place. A new line cancels whatever was pending —
    // the ladder's step, the «έτοιμο» hand-off — so an old timer never
    // wipes a new line mid-way (the handoff's §5.5 bug, avoided by design).
    function stopTimers() {
      if (statusTimer) { clearTimeout(statusTimer); statusTimer = null; }
      if (ladderTimer) { clearInterval(ladderTimer); ladderTimer = null; }
      if (readyTimer) { clearTimeout(readyTimer); readyTimer = null; }
    }
    function setStatus(kind, text, ms, withUndo) {
      stopTimers();
      statusKind = kind;
      status.setAttribute('data-lp-assist-status-kind', kind);
      status.textContent = text + (withUndo ? ' ' : '');
      if (withUndo) {
        var u = mk('button', 'lp-ai-undo', T.undo);
        u.type = 'button';
        u.setAttribute('data-lp-assist-undo', '');
        u.addEventListener('click', function () { undoInsert('status'); });
        status.appendChild(u);
      }
      if (ms) statusTimer = setTimeout(function () { clearStatus(kind); }, ms);
    }
    function clearStatus(kind) {
      if (kind && statusKind !== kind) return;
      stopTimers();
      statusKind = '';
      status.removeAttribute('data-lp-assist-status-kind');
      status.textContent = NBSP;
    }
    // The waiting ladder: «✦ Moodbook AI — Σκέφτομαι…», a step every 3 s,
    // the last step stays for as long as the call takes.
    function startLadder() {
      var step = 0;
      setStatus('wait', '✦ ' + label + ' — ' + T.ladder[0], 0);
      ladderTimer = setInterval(function () {
        step++;
        if (step >= T.ladder.length - 1) { step = T.ladder.length - 1; clearInterval(ladderTimer); ladderTimer = null; }
        if (statusKind === 'wait') status.textContent = '✦ ' + label + ' — ' + T.ladder[step];
      }, LADDER_MS);
    }

    // ── what the local checks need: the box, the face, the limits ─────────
    var canvas = null;
    function measurer(font) {
      if (!canvas) { try { canvas = document.createElement('canvas').getContext('2d'); } catch (e) { canvas = null; } }
      var fam = font.family ? '"' + font.family + '", serif' : 'serif';
      var up = font.transform === 'uppercase';
      if (canvas) { try { canvas.font = (font.weight || 700) + ' 100px ' + fam; } catch (e) { /* keep the last font */ } }
      return function (s) {
        var str = up ? String(s).toLocaleUpperCase('el-GR') : String(s);
        if (canvas) return canvas.measureText(str).width;
        return str.length * 55;   // no canvas at all: an average glyph — the rules still hold, the fit is approximate
      };
    }
    function ctx() {
      var boxW = 300, boxH = 150;
      if (cfg.boxPt && Number(cfg.boxPt.w) > 0 && Number(cfg.boxPt.h) > 0) { boxW = Number(cfg.boxPt.w); boxH = Number(cfg.boxPt.h); }
      else if (el.overlay && el.overlay.clientWidth > 0 && el.overlay.clientHeight > 0) { boxW = el.overlay.clientWidth; boxH = el.overlay.clientHeight; }
      var font = (el.state && el.state.font) || cfg.font || {};
      var spec = (el.fontSpecs && el.fontSpecs[font.family]) || {};
      var cap = Number(el.sizeMax || cfg.sizeMax) || 100;
      var sizePct = Math.min(Number(spec.sizePct || font.sizePct) || 80, cap);
      // A customer who has pressed − is driving the size: the cover draws at
      // their size, not at the fill, so the layout is judged at theirs.
      if (el.sizeTouched && Number(el.userSizePct) > 0) sizePct = Math.min(sizePct, Number(el.userSizePct));
      var floorPct = 4;
      try { if (typeof el.minSizePct === 'function') floorPct = el.minSizePct(); } catch (e) { /* the default floor */ }
      return {
        measure: measurer(font),
        boxW: boxW, boxH: boxH,
        leading: Number(spec.leading || font.leading) || 1.15,
        ceilPx: (boxH * sizePct) / 100,
        floorPx: (boxH * floorPct) / 100,
        maxLines: Number(cfg.assistMaxLines) || 4,
      };
    }
    function wordCount(t) { return (String(t || '').trim().match(/\S+/g) || []).length; }
    function norm(t) { return String(t || '').replace(/\s+/g, ' ').trim(); }
    // «Same text» the way the Worker judges it (sameText): case, accents,
    // final sigma, punctuation and spacing fall away — «…ιστορία σου» and
    // «…ιστορία σου.» are one text (review, 2026-09-20: the plain `norm`
    // let the full-stop twin land on the cover as «fresh»).
    function sameKey(t) {
      var v = String(t || '');
      try { v = v.normalize('NFD').replace(/\p{M}+/gu, '').toLowerCase().replace(/ς/g, 'σ').replace(/[^\p{L}\p{N}]+/gu, ''); }
      catch (e) { v = v.toLowerCase().replace(/ς/g, 'σ').replace(/[^0-9a-zα-ωϊϋ]+/gi, ''); }
      return v;
    }
    // The field's lines, as one line for the box: «Μαρία & Νίκος ⏎ 20/08/2027»
    // reads «Μαρία & Νίκος, 20/08/2027» — the shape of the example.
    function oneLine(t) { return String(t || '').split(/\s*\n+\s*/).map(function (s) { return s.trim(); }).filter(Boolean).join(', ').slice(0, MAX_ASK_CHARS); }

    // ── the local review: the accents, after a pause — no call ────────────
    function review() {
      if (open || !Tidy) return;
      var text = input.value || '';
      var flagged = text.trim() ? Tidy.accents(text) : [];
      if (flagged.length && !dismissed[norm(text)]) { acc.hidden = false; }
      else acc.hidden = true;
      writeRecord();
    }

    // ── the customer's answer travels with the order (2026-10-12) ─────────
    // The print queue held every cover with a flagged word until the owner
    // decided («✋ ΠΕΡΙΜΕΝΕΙ ΑΠΟΦΑΣΗ»). Stelios, 2026-10-12: the customer is
    // the one asked, so the answer goes with the order and the queue honours
    // it (tools/live-preview/accents.py → customer_answer()). One hidden line
    // item property, `properties[_lp_accents]`, compact JSON:
    //   { v: 1, a, w: [the words the line asked about], ms: time on screen }
    //   a = kept    «Είναι ΟΚ» / «Άσ’ το όπως ήταν» / the helper's own text,
    //               on THIS text            → printed as typed
    //       seen    the line was on screen ≥ 2 s for this text and the
    //               customer went on        → sure fixes in, the rest as typed
    //       unseen  flagged, never fairly in front of them → the owner decides
    // Disabled (so not sent at all) when nothing is flagged. It is kept
    // current on every change, not only at submit, so whichever listener
    // builds the cart request first reads the right answer.
    // ⚠ The words are the customer's own: they go to the ORDER, exactly like
    // the text in the field — never to analytics, never to the Worker.
    // 2 s on screen; `config.accentsSeenMs` overrides it (read each time: the harness shortens it).
    function seenLimit() { var c = el.config || cfg; return Number(c.accentsSeenMs) > 0 ? Number(c.accentsSeenMs) : 2000; }
    var record = document.createElement('input');
    record.type = 'hidden';
    record.name = 'properties[_lp_accents]';
    record.disabled = true;
    record.setAttribute('data-lp-accents-record', '');
    var recordForm = input.getAttribute('form') || (el.previewInput && el.previewInput.getAttribute('form'));
    if (recordForm) record.setAttribute('form', recordForm);
    el.appendChild(record);
    var seenMs = {};          // norm(text) → ms the accents line was on screen for that text
    var visFrom = 0;          // when the current on-screen stretch began
    var visKey = null;        // …and for which text
    var visTimer = null;
    // «On screen» means unhidden, at least half inside the viewport, in a tab
    // the customer is looking at. Without IntersectionObserver, unhidden is all
    // we can know.
    var onScreen = !window.IntersectionObserver;
    function lineVisible() { return !acc.hidden && onScreen && document.visibilityState !== 'hidden'; }
    function tick() {
      var now = Date.now();
      if (visFrom && visKey != null) seenMs[visKey] = (seenMs[visKey] || 0) + (now - visFrom);
      visFrom = 0; visKey = null;
      if (visTimer) { clearTimeout(visTimer); visTimer = null; }
      if (lineVisible()) {
        visFrom = now; visKey = norm(input.value);
        // crossing the threshold is a change too: no event fires for it
        visTimer = setTimeout(writeRecord, Math.max(0, seenLimit() - (seenMs[visKey] || 0)) + 50);
      }
    }
    function writeRecord() {
      if (visFrom) tick();      // bring a running stretch up to now
      var text = input.value || '';
      var flagged = (Tidy && text.trim()) ? Tidy.accents(text) : [];
      if (!flagged.length) { record.value = ''; record.disabled = true; return; }
      var k = norm(text);
      var ms = Math.round(seenMs[k] || 0);
      var words = [], had = {};
      flagged.forEach(function (f) { if (!had[f.word]) { had[f.word] = 1; words.push(f.word); } });
      record.value = JSON.stringify({ v: 1, a: dismissed[k] ? 'kept' : (ms >= seenLimit() ? 'seen' : 'unseen'), w: words, ms: ms });
      record.disabled = false;
    }
    el.lpAccentsRecord = writeRecord;   // the companion card's add asks for a fresh one
    new MutationObserver(function () { tick(); writeRecord(); }).observe(acc, { attributes: true, attributeFilter: ['hidden'] });
    if (window.IntersectionObserver) {
      new IntersectionObserver(function (entries) {
        onScreen = entries[entries.length - 1].intersectionRatio >= 0.5;
        tick();
        writeRecord();
      }, { threshold: [0, 0.5, 1] }).observe(acc);
    }
    document.addEventListener('visibilitychange', function () { tick(); writeRecord(); });
    // The pill reads what it will do; the box shows the skeleton and dims the
    // toolbar while a call is in flight.
    function refresh() {
      fab.disabled = busy;
      glyphText(fab, inserted ? T.undoChip : T.ideas);
      fab.setAttribute('data-lp-assist-fab', inserted ? 'undo' : 'ideas');
      fab.classList.toggle('lp-ai-fab-undo', inserted);
      box.classList.toggle('lp-ai-busy', busy && sheetMode !== 'spell');
      toneChips.forEach(function (c) { c.disabled = busy; });
      chipCustom.disabled = busy;
      go.disabled = busy;
      accShow.disabled = busy;
    }
    // The text's height follows the text: three lines at rest, a line more
    // for each line typed or inserted, eight at most (Stelios, 2026-09-20
    // evening: the box must be as tall as the text needs and no taller).
    var LINE_PX = 24, MIN_LINES = 3, MAX_LINES_SHOWN = 8;
    function fitHeight() {
      try {
        input.style.height = (MIN_LINES * LINE_PX) + 'px';
        var h = Math.max(MIN_LINES * LINE_PX, Math.min(MAX_LINES_SHOWN * LINE_PX, input.scrollHeight));
        input.style.height = h + 'px';
      } catch (e) { /* the CSS height stands */ }
    }
    fitHeight();
    window.addEventListener('resize', fitHeight);
    input.addEventListener('input', function () {
      fitHeight();
      if (writing) return;
      if (inserted) {
        // The customer is editing the helper's text: it is theirs now.
        inserted = false;
        prevText = null;
        track('ideas', 'edited', 'field', roundTone);
      }
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(review, IDLE_MS);
      refresh();
      if (statusKind === 'inserted' || statusKind === 'restored' || statusKind === 'ready') { /* the customer moved on */ clearStatus(statusKind); }
      if (!acc.hidden) acc.hidden = true;   // re-judged after the pause
      writeRecord();                        // a new text has no answer yet
    });
    input.addEventListener('blur', function () { if (idleTimer) clearTimeout(idleTimer); review(); });
    refresh();

    // The number that matters: an add-to-cart with the helper's text still on
    // the cover («kept»), or an edited version of it («kept_edited»).
    var formId = input.getAttribute('form') || (el.previewInput && el.previewInput.getAttribute('form'));
    var cartForm = formId ? document.getElementById(formId) : (input.closest ? input.closest('form') : null);
    if (cartForm) {
      cartForm.addEventListener('submit', function () {
        writeRecord();   // the answer as it stands at the tap
        if (roundText == null) return;
        track('ideas', norm(input.value) === norm(roundText) ? 'kept' : 'kept_edited', 'cart', roundTone);
      }, true);
    }

    // ── ideas: the pill swaps the panel in; a tone is the send ────────────
    fab.addEventListener('click', function () {
      if (busy) return;
      if (inserted) { undoInsert('fab'); return; }
      if (open) return;   // the pill is not shown while the panel is — a stray tap changes nothing
      openIdeas();
    });
    function openIdeas() {
      sheetMode = 'ideas';
      // The box holds the material. It follows the field until the customer
      // edits it — then it is theirs and the field no longer overwrites it.
      var fx = oneLine(input.value);
      if (ask.value === '' || ask.value === askFromField) { ask.value = fx; askFromField = fx; }
      glyphText(title, sheetTitle);
      body.textContent = '';
      body.appendChild(panel);
      renderMode();   // the chips, or the style box where the customer left it
      show();
      track('ideas', 'opened', 'fab');
    }
    // Enter in the box is not a send and not a newline: on a touch device it
    // puts the keyboard away; on a desktop focus stays where it is.
    ask.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); if (touchDevice()) { try { ask.blur(); } catch (err) { /* fine */ } } }
    });

    // ── the escape hatch: the chips give way to «‹», a box and «✨ Δημιουργία» ──
    // The panel stays. «‹» (or Escape in the box) brings the chips back at
    // once; the box keeps what was typed, and the mode is remembered across
    // openings so the same style again is the next answer, no call.
    function renderMode() {
      tonesBox.hidden = customMode;
      pickLine.hidden = customMode;
      customBox.hidden = !customMode;
      packChips();
    }
    // ── the chips in two rows at most (Stelios, 2026-09-20 evening) ────────
    // The flex wrap is greedy, and greedy fails where a fit exists: at 300px
    // three tones of 100/100/190 plus the fixed 128 wrap to three rows in
    // that order and to two with the long one first. So on opening (and on a
    // resize) every order of the tones is tried — the fixed «✏️ Άλλο ύφος...»
    // always last — and the first that fits two rows is the one shown; when
    // the given order already fits, nothing moves. If no order fits, the
    // chips tighten their padding once and try again. The chips keep their
    // index (data-lp-assist-tone, lp_tone): only their place in the row
    // changes.
    var CHIP_ROWS = 2;
    function packChips() {
      if (!open || customMode || tonesBox.hidden) return;
      var W = tonesBox.clientWidth;
      if (!(W > 0) || !toneChips.length) return;
      tonesBox.classList.remove('lp-ai-tones-tight');
      var best = fitChips(W);
      if (best.rows > CHIP_ROWS) {
        tonesBox.classList.add('lp-ai-tones-tight');
        var tight = fitChips(W);
        if (tight.rows < best.rows) best = tight; else tonesBox.classList.remove('lp-ai-tones-tight');
      }
      var same = best.order.every(function (idx, pos) { return tonesBox.children[pos] === toneChips[idx]; });
      if (!same) { best.order.forEach(function (idx) { tonesBox.appendChild(toneChips[idx]); }); }
      if (tonesBox.lastElementChild !== chipCustom) tonesBox.appendChild(chipCustom);
    }
    function fitChips(W) {
      var gap = parseFloat(getComputedStyle(tonesBox).columnGap || getComputedStyle(tonesBox).gap) || 8;
      var widths = toneChips.map(function (c) { return c.getBoundingClientRect().width; });
      var customW = chipCustom.getBoundingClientRect().width;
      var rowsOf = function (order) {
        var x = 0, rows = 1;
        var all = order.map(function (i) { return widths[i]; }).concat([customW]);
        for (var k = 0; k < all.length; k++) {
          var cw = all[k];
          if (x > 0 && x + gap + cw > W + 0.5) { rows++; x = cw; } else x = x > 0 ? x + gap + cw : cw;
        }
        return rows;
      };
      var identity = toneChips.map(function (c, i) { return i; });
      var best = { order: identity, rows: rowsOf(identity) };
      if (best.rows <= CHIP_ROWS) return best;
      permutations(identity).some(function (order) {
        var r = rowsOf(order);
        if (r < best.rows) best = { order: order, rows: r };
        return r <= CHIP_ROWS;   // the first that fits is the one shown
      });
      return best;
    }
    function permutations(arr) {
      if (arr.length <= 1) return [arr.slice()];
      var out = [];
      arr.forEach(function (x, i) {
        var rest = arr.slice(0, i).concat(arr.slice(i + 1));
        permutations(rest).forEach(function (p) { out.push([x].concat(p)); });
      });
      return out;
    }
    window.addEventListener('resize', function () { if (open) packChips(); });
    function enterCustom() {
      if (customMode || busy) return;
      customMode = true;
      renderMode();
      // They chose to type: the keyboard is welcome here, and the box is
      // kept above it.
      try { custom.focus({ preventScroll: true }); } catch (e) { try { custom.focus(); } catch (e2) { /* fine */ } }
      setTimeout(function () { ensureVisible(customBox); }, 60);
      track('ideas', 'custom', 'chip');
    }
    function leaveCustom() {
      if (!customMode) return;
      customMode = false;
      try { custom.blur(); } catch (e) { /* fine */ }
      renderMode();
      try { sheet.focus({ preventScroll: true }); } catch (e) { /* fine */ }
    }
    back.addEventListener('click', leaveCustom);
    go.addEventListener('click', goCustom);
    custom.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); goCustom(); }
      else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); leaveCustom(); }
    });
    custom.addEventListener('focus', function () { setTimeout(function () { if (open && document.activeElement === custom) ensureVisible(customBox); }, 250); });
    function goCustom() {
      var style = norm(custom.value).slice(0, MAX_CUSTOM_CHARS);
      if (!style) { try { custom.focus(); } catch (e) { /* fine */ } return; }
      sendTone(style, tones.length, true);
    }

    // A tone tap — or «✨ Δημιουργία». The material is the box for names —
    // what the field held when the panel opened, or what the customer typed
    // into it — never the helper's own text, so every tone this round is
    // about the same names and dates. Three answers per call; the same tone
    // again hands out the next one with no call, the fourth tap calls again
    // with the seen ones excluded. A style in the customer's own words is
    // its own set, keyed by the words.
    //
    // What a call must not repeat (2026-09-20): everything already shown for
    // this material — under THIS tone first, then under any other. Stelios
    // found the tones «did not differ»: the model's first answer for
    // «Κλασικό» was the text «Ρομαντικό» had just put on the cover. The
    // Worker's prompt now asks for a visibly different text per tone; this
    // list is the guarantee on our side, and it works with the Worker as
    // deployed. The list carries the helper's own texts only — never the
    // customer's — and is capped where the Worker caps it.
    function pickTone(i) { sendTone(tones[i], i, false); }
    function sendTone(tone, i, isCustom) {
      if (busy) return;
      var mat = norm(ask.value).slice(0, MAX_ASK_CHARS);
      var key = (isCustom ? 'custom:' : '') + tone + ' ' + mat;
      hide('pick');
      var q = queues[key];
      if (q && q.list.length) {
        // The queue hands out what has not been seen under ANY tone first: a
        // text another tone put on the cover meanwhile waits behind the fresh
        // ones (review, 2026-09-20) — and is still handed out last rather
        // than costing a call.
        q.list = unseenFirst(q.list, seenFor[mat] || []);
        insertIdea(q.list.shift(), i, q, 'next', false);
        return;
      }
      var exclude = seenList(q ? q.shown : [], seenFor[mat] || []);
      askModel('ideas', { tone: tone, toneIndex: i, custom: isCustom, text: mat, exclude: exclude, tones: tones.map(toneWord) }, function (list) {
        q = queues[key] = q || { list: [], shown: [], mat: mat };
        // A fresh answer that is already on the list of what they saw goes to
        // the end — the first one is what lands on the cover. If all three
        // were seen, the list stays as it came: a repeat beats an error.
        q.list = unseenFirst(list, seenFor[mat] || []);
        insertIdea(q.list.shift(), i, q, q.shown.length ? 'next' : 'inserted', true);
      });
    }
    // The same list, the texts not yet seen (under any tone) first, the seen
    // ones after them in their order — nothing dropped.
    function unseenFirst(list, seen) {
      var isSeen = function (s) { return seen.some(function (t) { return sameKey(t) === sameKey(s); }); };
      return list.filter(function (s) { return !isSeen(s); }).concat(list.filter(isSeen));
    }
    // This tone's own last three in the order they were seen, then the rest
    // most recent first; distinct; at most six (the Worker's cap).
    function seenList(own, all) {
      var out = [], keys = [];
      var push = function (s) { var k = sameKey(s); if (out.length < MAX_EXCLUDE && keys.indexOf(k) === -1) { out.push(s); keys.push(k); } };
      own.slice(-3).forEach(push);
      all.slice().reverse().forEach(push);
      return out;
    }
    // The answer goes onto the cover the moment «✦ Το κείμενο είναι έτοιμο.»
    // shows (after a call); the line under it follows 1.8 s later. A next
    // answer from the queue needs no ceremony.
    function insertIdea(text, i, q, result, fromCall) {
      if (!inserted) snapshot('ideas');
      inserted = true;
      roundText = text;
      roundTone = i;
      q.shown.push(text);
      (seenFor[q.mat] = seenFor[q.mat] || []).push(text);
      write(text);
      settle(text);
      dismissed[norm(text)] = true;   // what the helper wrote is not re-flagged
      writeRecord();                  // …and it is the customer's answer: they took it
      acc.hidden = true;
      if (fromCall) {
        setStatus('ready', T.ready, 0);
        readyTimer = setTimeout(function () { readyTimer = null; if (statusKind === 'ready') setStatus('inserted', T.insertedTone, STATUS_MS); }, READY_MS);
      } else {
        setStatus('inserted', T.insertedTone, STATUS_MS);
      }
      refresh();
      focusAfterPanel();
      track('ideas', result, 'tone', i);
    }

    // ── accents ───────────────────────────────────────────────────────────
    accShow.addEventListener('click', function () { track('spell', 'chip', 'accents_line'); askModel('spell', {}); });
    accOk.addEventListener('click', function () { dismissed[norm(input.value)] = true; acc.hidden = true; writeRecord(); track('spell', 'dismissed', 'accents_line'); });

    // ── the panel: in, and out ────────────────────────────────────────────
    close.addEventListener('click', function () { hide('close'); });
    document.addEventListener('keydown', function (e) { if (open && e.key === 'Escape') hide('escape'); });
    function show() {
      open = true;
      // The keyboard goes away: the panel is where the customer is now, and a
      // tap on a tone is the send. The box, if they want it, is a tap of its own.
      if (touchDevice()) { try { input.blur(); } catch (e) { /* keyboard stays, no harm */ } }
      box.classList.add('lp-ai-open');
      sheet.hidden = false;
      packChips();   // the chips are measurable now: two rows at most
      // The shop's chat launcher (<shopify-chat>, fixed at z-index 2147483000)
      // sat on the helper's controls on the phone (2026-09-18); the snippet
      // hides it while this class is on.
      document.documentElement.classList.add('lp-ai-sheet-on');
      // Focus lands on the panel itself — not on the names box, which would
      // raise the keyboard — and the whole box is brought into view.
      try { sheet.focus({ preventScroll: true }); } catch (e) { /* fine */ }
      setTimeout(function () { if (open) ensureVisible(box); }, 0);
      refresh();
    }
    function hide(why) {
      if (!open) return;
      open = false;
      box.classList.remove('lp-ai-open');
      sheet.hidden = true;
      document.documentElement.classList.remove('lp-ai-sheet-on');
      if (sheetMode === 'spell') {
        // A call still running for a card the customer closed is dropped:
        // the pill must not stay dead for 25 s, and a late answer has no
        // card to land on.
        if (busy && inflight) { try { inflight.abort(); } catch (e) { /* fine */ } }
        body.textContent = '';
        // The line that led here comes back if the question is still open.
        if (Tidy && input.value.trim() && !dismissed[norm(input.value)] && Tidy.accents(input.value).length) acc.hidden = false;
      }
      else if (why !== 'pick') track('ideas', 'cancelled', why || 'close');
      refresh();
      // Focus goes somewhere visible: the field on a desktop; the pill on a
      // touch device, where the field would raise the keyboard. After a
      // tone tap the answer is on its way — the field takes focus when it
      // lands (insertIdea) or when the call fails.
      if (why !== 'pick') focusAfterPanel();
    }
    function focusAfterPanel() {
      try {
        if (touchDevice()) { if (!fab.disabled) fab.focus({ preventScroll: true }); }
        else input.focus({ preventScroll: true });
      } catch (e) { /* fine */ }
    }

    // ── the ONE network call; everything the Worker gets is listed here ───
    // `spell` shows its answer on the card; `ideas` hands its list to `done`
    // and reports through the status line — there is no card for ideas.
    function askModel(kind, opts, done) {
      if (busy) return;
      mode = kind;
      var src = kind === 'ideas' ? 'tone' : 'accents_line';
      if (calls >= CALLS_PER_PAGE) {
        if (kind === 'spell' && open && sheetMode === 'spell') { body.textContent = ''; body.appendChild(mk('p', 'lp-ai-error', T.spent)); }
        else setStatus('spent', T.spent, 6000);
        track(kind, 'limit', src, opts.toneIndex);
        return;
      }
      calls++;
      busy = true;
      var waitLine = null;
      if (kind === 'spell') {
        sheetMode = 'spell';
        title.textContent = '';
        title.appendChild(star());
        title.appendChild(document.createTextNode(' ' + label));
        show();
        acc.hidden = true;   // the card IS the answer to that line
        body.textContent = '';
        waitLine = mk('p', 'lp-ai-wait', T.wait[kind]);
        body.appendChild(waitLine);
      } else {
        startLadder();
        refresh();
      }
      var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      inflight = ctrl;
      var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, TIMEOUT_MS) : null;
      var still = setTimeout(function () {
        if (waitLine && waitLine.isConnected) waitLine.textContent = T.wait[kind] + ' ' + T.stillWaiting;
      }, STILL_MS);
      var payload = {
        action: kind,
        text: kind === 'ideas' ? String(opts.text || '') : (input.value || ''),
        product: cfg.productTitle || cfg.product || '',
        productHandle: cfg.product || '',
        maxLines: Number(cfg.assistMaxLines) || 4,
      };
      var c = null;
      if (kind === 'ideas') {
        if (Tidy) {
          c = ctx();
          // Measured at the size the material forces (a name pair that the
          // fit will keep whole by shrinking), so the brief never asks the
          // impossible — see Tidy.capacity.
          var cap = Tidy.capacity(c, opts.text);
          payload.maxLines = cap.maxLines;
          payload.maxCharsPerLine = cap.maxCharsPerLine;
          payload.minChars = cap.minChars;
        }
        if (opts.tone) payload.tone = toneWord(opts.tone);
        if (opts.custom) payload.toneCustom = true;   // the customer's own words: the Worker fences them as data
        // The product's chips as words, so the model knows what the tapped
        // tone is NOT (2026-09-20) — labels, never anything the customer typed.
        if (opts.tones && opts.tones.length) payload.tones = opts.tones.slice(0, MAX_TONES);
        if (opts.exclude && opts.exclude.length) payload.exclude = opts.exclude.slice(0, MAX_EXCLUDE);
      }
      fetch(cfg.assistUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: ctrl ? ctrl.signal : undefined,
      })
        .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.json(); })
        .then(function (data) {
          if (kind === 'spell') { resultSpell(data); track('spell', 'shown', 'accents_line'); return; }
          var list = (data.suggestions || []).filter(function (s) { return typeof s === 'string' && s.trim(); }).map(noEmoji);
          var seenTexts = {};
          list = list.filter(function (s) { var k = sameKey(s); if (!k || seenTexts[k]) return false; seenTexts[k] = 1; return true; });
          // I2: an idea that does not fit the box is tidied, and dropped if it still does not.
          var dropped = 0;
          if (Tidy && c) {
            list = list.map(function (s) { var f = Tidy.fitIdea(s, c); if (f === null) dropped++; return f; }).filter(Boolean);
          }
          if (!list.length) { var e = new Error(dropped ? 'nofit' : 'empty'); e.nofit = dropped > 0; throw e; }
          busy = false;
          if (typeof done === 'function') done(list.slice(0, 3));
          if (dropped) track('ideas', 'shown_partial', 'tone', opts.toneIndex);
        })
        .catch(function (err) {
          if (kind === 'spell' && !open) return;   // the card was closed: nothing to show, nothing to report
          if (kind === 'spell') {
            body.textContent = '';
            body.appendChild(mk('p', 'lp-ai-error', T.error));
            var actions = mk('div', 'lp-ai-actions');
            var again = mk('button', 'lp-ai-secondary', T.retry);
            again.type = 'button';
            again.addEventListener('click', function () { askModel(kind, opts, done); });
            actions.appendChild(again);
            body.appendChild(actions);
          } else {
            // Back to the field as it was: the line says so, the text is untouched.
            setStatus('error', err && err.nofit ? T.noFit : T.error, ERROR_MS);
          }
          track(kind, err && err.nofit ? 'dropped' : 'error', src, opts.toneIndex);
        })
        .then(function () {
          if (timer) clearTimeout(timer);
          clearTimeout(still);
          if (inflight === ctrl) inflight = null;
          busy = false;
          refresh();
          if (kind === 'ideas' && !inserted && !open) focusAfterPanel();   // a failed call: back to the field
        });
    }

    // The correction: what changed and why, then two ways out.
    function resultSpell(data) {
      body.textContent = '';
      var corrected = String(data.corrected || '');
      var actions = mk('div', 'lp-ai-actions');
      if (!data.changed || !corrected || norm(corrected) === norm(input.value)) {
        body.appendChild(mk('p', 'lp-ai-fine', T.fine));
        var done = mk('button', 'lp-ai-secondary', T.ok);
        done.type = 'button';
        done.addEventListener('click', function () { dismissed[norm(input.value)] = true; acc.hidden = true; writeRecord(); hide('ok'); });
        actions.appendChild(done);
        body.appendChild(actions);
        return;
      }
      body.appendChild(mk('p', 'lp-ai-label', T.fixed + (data.note ? ' — ' + data.note : '')));
      var sug = mk('p', 'lp-ai-suggestion', corrected);
      sug.setAttribute('data-lp-assist-suggestion', '');
      body.appendChild(sug);
      var fix = mk('button', 'lp-ai-primary', T.fix);
      fix.type = 'button';
      fix.setAttribute('data-lp-assist-insert', '');
      fix.addEventListener('click', function () { apply(corrected, 'spell'); });
      var leave = mk('button', 'lp-ai-secondary', T.leave);
      leave.type = 'button';
      leave.setAttribute('data-lp-assist-leave', '');
      leave.addEventListener('click', function () { dismissed[norm(input.value)] = true; acc.hidden = true; writeRecord(); hide('leave'); track('spell', 'left', 'card'); });
      actions.appendChild(fix); actions.appendChild(leave);
      body.appendChild(actions);
    }

    function apply(text, kind) {
      if (!inserted) snapshot(kind);
      write(text);
      settle(text);
      hide('apply');
      acc.hidden = true;
      dismissed[norm(text)] = true;   // what the helper wrote is not re-flagged
      writeRecord();                  // …and it is the customer's answer: they took it
      setStatus('inserted', T.inserted, STATUS_MS, true);
      track(kind, 'inserted', 'card');
    }
    // What the field held before the helper's write — text AND size — so
    // «↩️ Αναίρεση» and the status line's ↶ can put both back at once,
    // through the same doors, even before live-preview-history.js has closed
    // its typing group.
    function snapshot(kind) {
      prevText = { text: input.value, size: el.userSizePct == null ? null : el.userSizePct, touched: !!el.sizeTouched, kind: kind || 'tidy' };
    }
    function undoInsert(from) {
      if (prevText == null) return;
      var back = prevText;
      var wasIdea = inserted;
      prevText = null;
      inserted = false;
      roundText = null;
      roundTone = -1;
      write(back.text);
      restoreSize(back.size, back.touched);
      clearStatus('inserted');
      clearStatus('ready');
      if (wasIdea) setStatus('restored', T.restored, RESTORED_MS);
      refresh();
      track(wasIdea ? 'ideas' : back.kind, 'undone', from || 'status');
      // The customer's text is back — and so is whatever it had earned: the
      // nudge, the accents line.
      if (idleTimer) clearTimeout(idleTimer);
      review();
    }
    // The one write. Bubbling `input` is what a keystroke fires, so the
    // preview, the floor, the descriptor — and the history — all follow.
    // `writing` tells this file's own input listener to stand still.
    function write(text) {
      writing = true;
      try {
        input.value = text;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      } finally { writing = false; }
      refresh();
    }
    // The cover's fit sizes by HEIGHT and re-wraps any line wider than the
    // box at that size — so a layout the helper wrote could come back as
    // lines nobody chose («Save / the Date»). When that would happen, the
    // size is lowered to the one that keeps every line whole, through the
    // stepper's own door (userSizePct + sizeTouched + renderForUser), the
    // same one ↶ ↷ restore. Stelios's «font size micro auto adjust».
    function settle(text) {
      if (!Tidy) return;
      var c = ctx();
      var L = Tidy.layoutSize(text, c);
      if (!L || !L.rewrap || !(L.size > 0) || !(c.boxH > 0)) return;
      restoreSize(Math.round(((L.size / c.boxH) * 100) * 10) / 10, true);
    }
    function restoreSize(pct, touched) {
      el.userSizePct = pct;
      el.sizeTouched = !!touched;
      try { if (typeof el.renderForUser === 'function') el.renderForUser(); } catch (e) { /* the next input re-renders */ }
    }

    function track(m, result, source, toneIndex) {
      var params = { lp_mode: m || 'ideas', lp_result: result, lp_source: source || 'chip' };
      if (typeof toneIndex === 'number' && toneIndex >= 0) params.lp_tone = toneIndex + 1;
      if (Tidy) params.lp_shape = Tidy.shapeOf(input.value);
      try { if (typeof window.gtag === 'function') window.gtag('event', 'lp_assist', params); } catch (e) { /* analytics never breaks the shop */ }
      try {
        window.dataLayer = window.dataLayer || [];
        var row = { event: 'lp_assist' };
        for (var k in params) if (Object.prototype.hasOwnProperty.call(params, k)) row[k] = params[k];
        window.dataLayer.push(row);
      } catch (e) { /* same */ }
    }

    function chip(kind, text) {
      var b = mk('button', 'lp-ai-chip');
      glyphText(b, text);
      b.type = 'button';
      b.setAttribute('data-lp-assist-chip', kind);
      return b;
    }
  }

  // A leading emoji is decoration: it goes into an aria-hidden span so a
  // screen reader says «Δώσε μου ιδέες», not «sparkles Δώσε μου ιδέες».
  // textContent of the element stays the full label.
  function glyphText(el, text) {
    var m = null;
    try { m = /^(\p{Extended_Pictographic}(?:\uFE0F|\u200D\p{Extended_Pictographic}\uFE0F?)*)\s+/u.exec(text); } catch (e) { m = null; }
    el.textContent = '';
    if (m) {
      var g = document.createElement('span');
      g.setAttribute('aria-hidden', 'true');
      g.textContent = m[1] + ' ';
      el.appendChild(g);
      el.appendChild(document.createTextNode(text.slice(m[0].length)));
    } else {
      el.textContent = text;
    }
  }

  // The product's tone chips: strings, trimmed, distinct, at most five; the
  // generic three when the product has none.
  function toneList(raw) {
    var out = [], seen = {};
    if (Array.isArray(raw)) {
      for (var i = 0; i < raw.length && out.length < MAX_TONES; i++) {
        var s = String(raw[i] == null ? '' : raw[i]).replace(/\s+/g, ' ').trim().slice(0, 40);
        if (!s || seen[s]) continue;
        seen[s] = 1;
        out.push(s);
      }
    }
    return out.length ? out : T.defaultTones.slice();
  }
  // The chip's emoji is for the customer; the Worker gets the word.
  function toneWord(label) {
    try { return String(label).replace(/[^\p{L}\p{N}\s\/&'’,.-]/gu, ' ').replace(/\s+/g, ' ').trim(); }
    catch (e) { return String(label).replace(/[^ -~Ͱ-Ͽἀ-῿]/g, ' ').replace(/\s+/g, ' ').trim(); }
  }
  // Nothing the cover's faces cannot print: an emoji in an idea would go to
  // the press as a missing glyph.
  function noEmoji(s) {
    try { return String(s).replace(/[\p{Extended_Pictographic}️‍]/gu, '').replace(/[ \t]+/g, ' ').replace(/ *\n */g, '\n').trim(); }
    catch (e) { return String(s).replace(/[\uD83C-\uDBFF][\uDC00-\uDFFF]|[☀-➿️‍]/g, '').trim(); }
  }

  // The one accent of colour: an amber four-point star, inline SVG so it
  // renders the same on every device (design rev 02 §ΣΤ).
  function star() {
    var s = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    s.setAttribute('viewBox', '0 0 24 24');
    s.setAttribute('aria-hidden', 'true');
    s.setAttribute('class', 'lp-ai-star');
    var p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    p.setAttribute('d', 'M12 2l2.2 6.3L20.5 10.5 14.2 12.7 12 19 9.8 12.7 3.5 10.5 9.8 8.3z');
    s.appendChild(p);
    return s;
  }
  function isMobile() {
    try { return window.matchMedia(MOBILE).matches; } catch (e) { return false; }
  }
  // A touch device of any width raises its keyboard on focus: an iPad in
  // landscape is «desktop» by width and a phone by pointer.
  function touchDevice() {
    try { return window.matchMedia('(pointer: coarse)').matches || isMobile(); } catch (e) { return isMobile(); }
  }
  function mk(tag, cls, text) {
    var e = document.createElement(tag);
    e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  function boot() {
    var all = document.querySelectorAll('live-preview');
    for (var i = 0; i < all.length; i++) init(all[i]);
  }
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('live-preview').then(boot);
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.LivePreviewAssist = { init: init };
})();
